import React from "react";

export default function Terms() {
  return (
    <div className="pbp-section py-12 md:py-16 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Legal</div>
      <h1 className="mt-2 text-4xl font-semibold tracking-tighter">Terms &amp; Conditions</h1>
      <p className="mt-2 text-sm text-[#86868b]">Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="mt-10 space-y-8 prose-pbp">
        <Section n="1" title="About PartByPart">
          PartByPart (&ldquo;Platform&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is an online peer-to-peer
          marketplace operated from India that enables individuals worldwide to list, search and
          communicate about used, partially working, or non-functional Apple device parts and
          accessories. The Platform is provided by the operator of PartByPart, and these Terms are
          governed by the laws of India.
        </Section>

        <Section n="2" title="Acceptance of Terms">
          By accessing or using PartByPart, you confirm that you are at least 18 years old and have
          the legal capacity to enter into a binding contract under the Indian Contract Act, 1872. If
          you do not agree with any part of these Terms, you must not use the Platform.
        </Section>

        <Section n="3" title="Nature of Service & Intermediary Status">
          PartByPart functions as an &ldquo;intermediary&rdquo; as defined under Section 2(1)(w) of the
          Information Technology Act, 2000. We do not own, sell, resell, store, or warrant any of the
          parts listed by users. We are not a party to any transaction between buyers and sellers and
          claim safe harbour protection under Section 79 of the Information Technology Act, 2000 and
          the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules,
          2021.
        </Section>

        <Section n="4" title="User Accounts">
          Sign-in is via Google OAuth 2.0. You are responsible for safeguarding access to your Google
          account and for any activity that occurs under your PartByPart session. We may suspend or
          terminate accounts that violate these Terms, our community guidelines, or applicable law.
        </Section>

        <Section n="5" title="Listings — Permitted & Prohibited">
          <p>You may list genuine Apple-branded parts you legally own. Listings must disclose the
          true condition. You must not list:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Stolen goods, lost-mode devices, iCloud-locked iPhones/iPads, or items whose ownership you cannot demonstrate;</li>
            <li>Counterfeit, refurbished-as-genuine, or non-Apple parts labelled as Apple;</li>
            <li>Items prohibited by Indian law, the IT Rules 2021, or the destination country&rsquo;s law;</li>
            <li>Items containing personal data of third parties (you must factory-reset devices before listing);</li>
            <li>Hazardous materials, including swollen batteries, without clear warnings.</li>
          </ul>
          <p>We use automated checks (AI moderation, duplicate detection, keyword screening) and
          human review. Listings flagged three or more times by users will be auto-suspended pending
          review.</p>
        </Section>

        <Section n="6" title="Transactions Take Place Off-Platform">
          PartByPart does not process payments, escrow funds, or arrange shipping. All sale,
          purchase, payment, delivery, refund and dispute resolution arrangements are solely between
          you and the other user. We strongly recommend meeting at a public place, inspecting the
          product, and using identifiable payment methods.
        </Section>

        <Section n="7" title="User Conduct">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Post content that is unlawful, obscene, defamatory, harassing, or harmful to minors;</li>
            <li>Impersonate any person, misrepresent location or identity, or post false listings;</li>
            <li>Use the chat system to solicit payments outside of a genuine product transaction;</li>
            <li>Scrape, reverse-engineer or otherwise extract data from the Platform;</li>
            <li>Upload viruses, malware, or anything that disrupts the Platform.</li>
          </ul>
        </Section>

        <Section n="8" title="Fees">
          Use of PartByPart is currently free. We may introduce paid features (promoted listings,
          verification badges, premium tiers) with notice. You will be informed of any such fees
          before they apply to you.
        </Section>

        <Section n="9" title="Intellectual Property">
          PartByPart, the logo, and all Platform content (excluding user-submitted content) are owned
          by us. You retain ownership of content you upload and grant PartByPart a worldwide,
          non-exclusive, royalty-free licence to host, display and promote your listing for as long
          as it is active. &ldquo;Apple&rdquo;, &ldquo;iPhone&rdquo;, &ldquo;iPad&rdquo;, &ldquo;Mac&rdquo;,
          &ldquo;AirPods&rdquo; and related marks are trademarks of Apple Inc. PartByPart is not
          affiliated with, endorsed by, or sponsored by Apple Inc.
        </Section>

        <Section n="10" title="Disclaimers & Limitation of Liability">
          The Platform is provided &ldquo;as is&rdquo; without warranties of any kind. To the maximum
          extent permitted by law, PartByPart shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or any loss of profits, revenue, data, or
          goodwill, arising out of or in connection with your use of the Platform or any transaction
          between users. Our aggregate liability shall not exceed INR 1,000.
        </Section>

        <Section n="11" title="Indemnity">
          You agree to indemnify and hold harmless PartByPart, its operators, employees and partners
          from any claim, demand, or damages, including reasonable legal fees, arising from your use
          of the Platform, your listings, your chats, or your transactions.
        </Section>

        <Section n="12" title="Reporting & Grievance Redressal">
          In compliance with the IT Rules 2021, we publish the contact information of our Grievance
          Officer. To report unlawful content or to raise a grievance, write to
          <b> grievance@partbypart.com</b>. The Grievance Officer will acknowledge complaints within
          24 hours and resolve them within 15 days.
        </Section>

        <Section n="13" title="Suspension & Termination">
          We may suspend, restrict, or terminate your access at any time for breach of these Terms,
          for protecting the safety of other users, or as required by law, with or without notice.
        </Section>

        <Section n="14" title="Governing Law & Jurisdiction">
          These Terms are governed by, and construed in accordance with, the laws of the Republic of
          India, including without limitation the Indian Contract Act, 1872; the Sale of Goods Act,
          1930; the Consumer Protection Act, 2019 (and the Consumer Protection (E-Commerce) Rules,
          2020); the Information Technology Act, 2000 (read with the Intermediary Guidelines, 2021);
          the Digital Personal Data Protection Act, 2023; the Goods and Services Tax Act, 2017
          (where applicable to a seller&rsquo;s business); the Indian Penal Code, 1860; the Foreign
          Exchange Management Act, 1999 (for cross-border transactions); and the Trade Marks Act,
          1999. Subject to mandatory consumer-protection venue rules, the courts and tribunals at
          <b> Mumbai, Maharashtra, India</b> shall have <b>exclusive jurisdiction</b> over any
          dispute arising out of or in connection with these Terms or your use of the Platform. You
          waive any objection of <i>forum non conveniens</i> with respect to such courts.
        </Section>

        <Section n="15" title="Dispute Resolution & Arbitration">
          Before initiating litigation, the parties shall in good faith attempt to resolve any
          dispute through written notice followed by 30 days of negotiation. Failing amicable
          settlement, the dispute shall be referred to <b>arbitration under the Arbitration and
          Conciliation Act, 1996</b> by a sole arbitrator appointed mutually (or, failing
          agreement, by the Bombay High Court). The seat and venue of arbitration shall be
          <b> Mumbai, Maharashtra</b>; the language English; the award final and binding. Nothing in
          this clause prevents either party from seeking interim injunctive relief from the courts
          at Mumbai.
        </Section>

        <Section n="16" title="Changes to Terms">
          We may update these Terms from time to time. Material changes will be notified via in-app
          banner or email at least 14 days in advance. Continued use of the Platform after updates
          constitutes acceptance.
        </Section>

        <Section n="17" title="Contact">
          Operator: PartByPart, Mumbai, Maharashtra, India.<br />
          Email: <a className="pbp-link" href="mailto:support@partbypart.com">support@partbypart.com</a><br />
          Grievance Officer: <a className="pbp-link" href="mailto:grievance@partbypart.com">grievance@partbypart.com</a>
        </Section>
      </div>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="text-xl font-semibold tracking-tight"><span className="text-[#86868b] mr-3">{n}.</span>{title}</h2>
    <div className="mt-3 text-[#1d1d1f]/85 leading-relaxed space-y-3">{children}</div>
  </section>
);
